import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { TaskPriority } from "@/types/task";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Plus, X, Sparkles, FolderPlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { Category } from "@/hooks/useCategories";

const QUICK_COLORS = ["#6366f1", "#ec4899", "#22c55e", "#f97316", "#3b82f6", "#8b5cf6"];
const QUICK_ICONS = ["📁", "🏢", "💼", "🎨", "🛒", "💡"];

interface TaskFormProps {
  onAddTask: (title: string, duration: number, priority: TaskPriority, categoryId?: string) => void;
  categories?: Category[];
  initialTitle?: string;
  initialDuration?: number;
  initialPriority?: TaskPriority;
  initialCategoryId?: string;
  submitLabel?: string;
  onCancel?: () => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onManageCategories?: () => void;
  onQuickAddCategory?: (name: string, color: string, icon: string) => void;
}

const TaskForm = ({ 
  onAddTask, 
  categories = [],
  initialTitle = "", 
  initialDuration = 0, 
  initialPriority = "normal",
  initialCategoryId = "",
  submitLabel = "הוסף",
  onCancel,
  isOpen = false,
  onOpenChange,
  onManageCategories,
  onQuickAddCategory,
}: TaskFormProps) => {
  const [title, setTitle] = useState(initialTitle);
  const [duration, setDuration] = useState(initialDuration);
  const [priority, setPriority] = useState<TaskPriority>(initialPriority);
  const [categoryId, setCategoryId] = useState(initialCategoryId);
  const [expanded, setExpanded] = useState(isOpen);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [newCatColor, setNewCatColor] = useState(QUICK_COLORS[0]);
  const [newCatIcon, setNewCatIcon] = useState(QUICK_ICONS[0]);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();
  
  useEffect(() => {
    setExpanded(isOpen);
    if (isOpen) {
      setTitle(initialTitle);
      setDuration(initialDuration);
      setPriority(initialPriority);
      setCategoryId(initialCategoryId);
    }
  }, [isOpen, initialTitle, initialDuration, initialPriority, initialCategoryId]);
  
  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(expanded);
    }
  }, [expanded, onOpenChange]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (title.trim()) {
      onAddTask(title, duration || 0, priority || "normal", categoryId || undefined);
      setTitle("");
      setDuration(0);
      setPriority("normal");
      setCategoryId("");
      setExpanded(false);
    }
  };
  
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      setExpanded(false);
      setTitle(initialTitle);
      setDuration(initialDuration);
      setPriority(initialPriority);
      setCategoryId(initialCategoryId);
    }
  };

  useEffect(() => {
    if (expanded && titleInputRef.current) {
      setTimeout(() => {
        titleInputRef.current?.focus();
      }, 100);
    }
  }, [expanded]);

  const getPriorityGradient = () => {
    switch (priority) {
      case 'low':
        return 'from-task-low to-emerald-500';
      case 'normal':
        return 'from-task-normal to-amber-500';
      case 'high':
        return 'from-task-high to-rose-500';
      default:
        return 'from-primary to-primary-glow';
    }
  };

  return (
    <div className="w-full">
      <AnimatePresence mode="wait">
        {!expanded ? (
          <motion.div
            key="button"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <Button 
              onClick={() => setExpanded(true)}
              className={cn(
                "w-full group relative overflow-hidden",
                "bg-primary hover:bg-primary/90",
                "text-primary-foreground font-semibold",
                "flex items-center justify-center gap-3 text-lg mb-3",
                "border-0",
                "rounded-2xl shadow-md hover:shadow-xl hover:shadow-primary/20",
                "transition-all duration-300"
              )}
              size={isMobile ? "lg" : "default"}
            >
              <motion.div
                whileHover={{ rotate: 90 }}
                transition={{ duration: 0.3 }}
              >
                <Plus className="h-6 w-6" />
              </motion.div>
              <span>הוסף משימה חדשה</span>
              <Sparkles className="h-4 w-4 opacity-50 group-hover:opacity-100 transition-opacity" />
            </Button>
          </motion.div>
        ) : (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="relative overflow-hidden rounded-2xl shadow-xl border border-border/50 bg-card/98 backdrop-blur-md"
          >
            {/* Decorative gradient header */}
            <div className={cn(
              "h-2 bg-gradient-to-r",
              getPriorityGradient()
            )} />
            
            <form onSubmit={handleSubmit} className="p-5 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="title" className="text-right font-medium text-foreground/80">
                  כותרת המשימה
                </Label>
                <Input
                  ref={titleInputRef}
                  id="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="מה צריך לעשות?"
                  className="text-right rtl:text-right h-12 text-lg border-2 border-border/50 focus:border-primary/50 rounded-xl bg-background/50 transition-all duration-300"
                  style={{ textAlign: 'right', direction: 'rtl' }}
                  autoComplete="off"
                  required
                />
              </div>

              {/* Category selector */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-right font-medium text-foreground/80">
                    קטגוריה
                  </Label>
                  <div className="flex items-center gap-2">
                    {onQuickAddCategory && (
                      <button
                        type="button"
                        onClick={() => setShowQuickCreate(!showQuickCreate)}
                        className="text-xs text-primary hover:underline flex items-center gap-1"
                      >
                        <FolderPlus className="h-3 w-3" />
                        צור חדשה
                      </button>
                    )}
                    {onManageCategories && (
                      <button
                        type="button"
                        onClick={onManageCategories}
                        className="text-xs text-muted-foreground hover:text-primary hover:underline"
                      >
                        נהל
                      </button>
                    )}
                  </div>
                </div>

                {/* Quick create inline */}
                <AnimatePresence>
                  {showQuickCreate && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 p-3 space-y-2">
                        <Input
                          value={newCatName}
                          onChange={(e) => setNewCatName(e.target.value)}
                          placeholder="שם הקטגוריה..."
                          className="h-9 text-sm text-right border-border/50 rounded-lg bg-background/80"
                          style={{ textAlign: "right", direction: "rtl" }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (newCatName.trim() && onQuickAddCategory) {
                                onQuickAddCategory(newCatName.trim(), newCatColor, newCatIcon);
                                setNewCatName("");
                                setShowQuickCreate(false);
                              }
                            }
                          }}
                        />
                        <div className="flex items-center gap-1.5">
                          {QUICK_COLORS.map((c) => (
                            <button
                              key={c}
                              type="button"
                              onClick={() => setNewCatColor(c)}
                              className={cn(
                                "w-6 h-6 rounded-full transition-all border-2",
                                newCatColor === c ? "border-foreground scale-110" : "border-transparent hover:scale-105"
                              )}
                              style={{ backgroundColor: c }}
                            />
                          ))}
                        </div>
                        <div className="flex items-center gap-1">
                          {QUICK_ICONS.map((i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => setNewCatIcon(i)}
                              className={cn(
                                "w-7 h-7 rounded-md flex items-center justify-center text-sm border-2",
                                newCatIcon === i ? "border-primary bg-primary/10" : "border-transparent hover:bg-muted"
                              )}
                            >
                              {i}
                            </button>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            disabled={!newCatName.trim()}
                            onClick={() => {
                              if (newCatName.trim() && onQuickAddCategory) {
                                onQuickAddCategory(newCatName.trim(), newCatColor, newCatIcon);
                                setNewCatName("");
                                setShowQuickCreate(false);
                              }
                            }}
                            className="rounded-lg gap-1 h-8 text-xs"
                          >
                            <Check className="h-3 w-3" />
                            צור
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => setShowQuickCreate(false)}
                            className="rounded-lg h-8 text-xs"
                          >
                            ביטול
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {categories.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {categories.map((cat) => (
                      <motion.button
                        key={cat.id}
                        type="button"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setCategoryId(categoryId === cat.id ? "" : cat.id)}
                        className={cn(
                          "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 border",
                          categoryId === cat.id
                            ? "border-2 shadow-md"
                            : "bg-muted/50 text-muted-foreground border-border/50 hover:bg-accent hover:text-accent-foreground"
                        )}
                        style={categoryId === cat.id ? { 
                          backgroundColor: cat.color + "20", 
                          borderColor: cat.color, 
                          color: cat.color 
                        } : undefined}
                      >
                        <span>{cat.icon}</span>
                        <span>{cat.name}</span>
                      </motion.button>
                    ))}
                  </div>
                ) : !showQuickCreate ? (
                  <button
                    type="button"
                    onClick={() => onQuickAddCategory ? setShowQuickCreate(true) : onManageCategories?.()}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors py-2 flex items-center gap-1.5"
                  >
                    <FolderPlus className="h-4 w-4" />
                    צור את הקטגוריה הראשונה שלך
                  </button>
                ) : null}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="duration" className="text-right font-medium text-foreground/80">
                    משך זמן (דקות)
                  </Label>
                  <Input
                    id="duration"
                    type="number"
                    value={duration}
                    onChange={(e) => setDuration(Number(e.target.value))}
                    min={0}
                    step={5}
                    className="text-right h-12 text-lg border-2 border-border/50 focus:border-primary/50 rounded-xl bg-background/50 transition-all duration-300"
                    style={{ textAlign: 'right', direction: 'rtl' }}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="priority" className="text-right font-medium text-foreground/80">
                    עדיפות
                  </Label>
                  <Select 
                    value={priority} 
                    onValueChange={(value) => setPriority(value as TaskPriority)}
                  >
                    <SelectTrigger 
                      id="priority" 
                      className="w-full h-12 text-right border-2 border-border/50 focus:border-primary/50 rounded-xl bg-background/50 transition-all duration-300"
                    >
                      <SelectValue placeholder="בחר עדיפות" />
                    </SelectTrigger>
                    <SelectContent position="popper" className="w-full rounded-xl border-2" dir="rtl">
                      <SelectItem value="low" className="text-right rounded-lg">
                        <div className="flex items-center gap-3 w-full justify-end py-1">
                          <span className="font-medium">נמוכה</span>
                          <motion.span 
                            className="h-3 w-3 rounded-full bg-task-low shadow-sm shadow-task-low/50"
                            whileHover={{ scale: 1.2 }}
                          />
                        </div>
                      </SelectItem>
                      <SelectItem value="normal" className="text-right rounded-lg">
                        <div className="flex items-center gap-3 w-full justify-end py-1">
                          <span className="font-medium">רגילה</span>
                          <motion.span 
                            className="h-3 w-3 rounded-full bg-task-normal shadow-sm shadow-task-normal/50"
                            whileHover={{ scale: 1.2 }}
                          />
                        </div>
                      </SelectItem>
                      <SelectItem value="high" className="text-right rounded-lg">
                        <div className="flex items-center gap-3 w-full justify-end py-1">
                          <span className="font-medium">גבוהה</span>
                          <motion.span 
                            className="h-3 w-3 rounded-full bg-task-high animate-pulse shadow-sm shadow-task-high/50"
                            whileHover={{ scale: 1.2 }}
                          />
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={handleCancel}
                  className="flex-1 h-12 flex items-center justify-center gap-2 rounded-xl border-2 border-border/50 hover:border-destructive/50 hover:bg-destructive/5 hover:text-destructive transition-all duration-300"
                >
                  <X className="h-5 w-5" />
                  <span className="font-medium">{onCancel ? "בטל" : "סגור"}</span>
                </Button>
                <Button 
                  type="submit" 
                  className={cn(
                    "flex-1 h-12 flex items-center justify-center gap-2 rounded-xl font-medium text-primary-foreground shadow-lg transition-all duration-300 hover:shadow-xl",
                    `bg-gradient-to-r ${getPriorityGradient()}`,
                    "hover:opacity-90"
                  )}
                >
                  <Check className="h-5 w-5" />
                  <span>{submitLabel}</span>
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TaskForm;
